/**
 * Audio-player control wiring — every input surface that drives playback.
 *
 * Owns the event listeners for:
 *   - Player play/pause toggle (apPlayPause)
 *   - Rewind 15s + forward 30s
 *   - Close (stop)
 *   - Speed cycle (0.75× → 2×)
 *   - Volume slider
 *   - Seek-bar scrub (mouse + touch + keyboard)
 *   - Document-level keyboard shortcuts (Space / ArrowLeft / ArrowRight)
 *
 * All audio mutations route through the supplied audio-session controller
 * (the result of {@link createAudioSession}). The lib never touches an
 * HTMLAudioElement directly — that's a hard discipline boundary, so the
 * session is the single source of truth for "what's playing".
 *
 * Returns a disposer that detaches every listener. Safe to invoke when
 * any/all refs are missing.
 *
 * Build path:
 *   src   assets/js/src/single-article/audio-controls.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/audio-controls.js  (attaches window.XOT.article.audioControls)
 *
 * @since Phase 2 — JS modernization, bite 13
 */

const DEFAULT_SPEEDS:     ReadonlyArray<number> = [0.75, 1, 1.25, 1.5, 1.75, 2];
const DEFAULT_SKIP_BACK:  number = 15;
const DEFAULT_SKIP_FWD:   number = 30;
const KB_SKIP:            number = 10;
const SEEK_KB_STEP:       number = 5;

/** Minimal session-controller surface needed by the controls. */
export interface AudioSessionLike {
    el():        HTMLAudioElement | null;
    isPaused():  boolean;
    pause():     void;
    resume():    void;
    seekTo(t: number): void;
    setRate(rate: number): void;
    setVolume(v: number): void;
    stop():      void;
}

export interface AudioControlsRefs {
    apPlayPause?: HTMLElement      | null;
    rewindBtn?:   HTMLElement      | null;
    forwardBtn?:  HTMLElement      | null;
    closeBtn?:    HTMLElement      | null;
    speedBtn?:    HTMLElement      | null;
    volSlider?:   HTMLInputElement | null;
    seekWrap?:    HTMLElement      | null;
    player?:      HTMLElement      | null;
}

export interface AudioControlsDeps {
    /** Returns the currently-active session, or null when no audio is loaded. */
    getSession: () => AudioSessionLike | null;
    /** UI callback after a successful play / resume. */
    onPlay?:    () => void;
    /** UI callback after a successful pause. */
    onPause?:   () => void;
    /** Optional UI hook after a seek (e.g., to refresh the progress fill). */
    onSeek?:    () => void;
    /** Optional speed list — defaults to 0.75, 1, 1.25, 1.5, 1.75, 2. */
    speeds?:    ReadonlyArray<number>;
    /** Initial speed index — defaults to 1 (= 1×). */
    initialSpeedIndex?: number;
    /** Skip seconds on rewind/forward — defaults 15 / 30. */
    skipBack?:  number;
    skipFwd?:   number;
}

/**
 * Wire every control surface and return a disposer.
 * The disposer removes every listener attached during init.
 */
export function initAudioControls(refs: AudioControlsRefs, deps: AudioControlsDeps): () => void {
    const speeds      = deps.speeds   ?? DEFAULT_SPEEDS;
    const skipBack    = deps.skipBack ?? DEFAULT_SKIP_BACK;
    const skipFwd     = deps.skipFwd  ?? DEFAULT_SKIP_FWD;
    let speedIndex    = deps.initialSpeedIndex ?? 1;

    /* ── per-handler closures so the dispose() removeEventListener calls
       resolve to the same function references that were added. */

    function onPlayPauseClick(): void {
        const s = deps.getSession();
        if (!s) return;
        if (s.isPaused()) {
            s.resume();
            deps.onPlay?.();
        } else {
            s.pause();
            deps.onPause?.();
        }
    }

    function onRewindClick(): void {
        const s = deps.getSession();
        const el = s?.el();
        if (!s || !el) return;
        s.seekTo(Math.max(0, el.currentTime - skipBack));
        deps.onSeek?.();
    }

    function onForwardClick(): void {
        const s = deps.getSession();
        const el = s?.el();
        if (!s || !el || !el.duration) return;
        s.seekTo(Math.min(el.duration, el.currentTime + skipFwd));
        deps.onSeek?.();
    }

    function onCloseClick(): void {
        deps.getSession()?.stop();
    }

    function onSpeedClick(): void {
        speedIndex = (speedIndex + 1) % speeds.length;
        const rate = speeds[speedIndex];
        if (refs.speedBtn) refs.speedBtn.textContent = rate + '×';
        deps.getSession()?.setRate(rate);
    }

    function onVolumeInput(this: HTMLInputElement): void {
        deps.getSession()?.setVolume(parseFloat(this.value));
    }

    let scrubbing = false;

    function handleSeek(e: MouseEvent | TouchEvent): void {
        if (!refs.seekWrap) return;
        const s = deps.getSession();
        const el = s?.el();
        if (!s || !el || !el.duration) return;
        const rect    = refs.seekWrap.getBoundingClientRect();
        const clientX = (e as TouchEvent).touches?.[0]?.clientX ?? (e as MouseEvent).clientX;
        const pct     = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        s.seekTo(pct * el.duration);
        deps.onSeek?.();
    }

    function onSeekMouseDown(e: MouseEvent):   void { scrubbing = true; handleSeek(e); }
    function onSeekTouchStart(e: TouchEvent):  void { scrubbing = true; handleSeek(e); }
    function onDocMouseMove(e: MouseEvent):    void { if (scrubbing) handleSeek(e); }
    function onDocMouseUp():                   void { scrubbing = false; }
    function onDocTouchEnd():                  void { scrubbing = false; }

    function onSeekKeyDown(e: KeyboardEvent): void {
        const s = deps.getSession();
        const el = s?.el();
        if (!s || !el || !el.duration) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            s.seekTo(Math.min(el.duration, el.currentTime + SEEK_KB_STEP));
            deps.onSeek?.();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            s.seekTo(Math.max(0, el.currentTime - SEEK_KB_STEP));
            deps.onSeek?.();
        }
    }

    function onDocKeyDown(e: KeyboardEvent): void {
        const target = e.target as Element | null;
        const tag    = target?.tagName ?? '';
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        const playerActive = !!refs.player?.classList.contains('active');
        if (!playerActive) return;

        if (e.code === 'Space') {
            e.preventDefault();
            refs.apPlayPause?.click();
            return;
        }
        const s  = deps.getSession();
        const el = s?.el();
        if (!s || !el || !el.duration) return;
        if (e.code === 'ArrowRight') {
            e.preventDefault();
            s.seekTo(Math.min(el.duration, el.currentTime + KB_SKIP));
            deps.onSeek?.();
        } else if (e.code === 'ArrowLeft') {
            e.preventDefault();
            s.seekTo(Math.max(0, el.currentTime - KB_SKIP));
            deps.onSeek?.();
        }
    }

    /* ── attach ─────────────────────────────────────────────────────── */
    refs.apPlayPause?.addEventListener('click', onPlayPauseClick);
    refs.rewindBtn?.addEventListener('click',   onRewindClick);
    refs.forwardBtn?.addEventListener('click',  onForwardClick);
    refs.closeBtn?.addEventListener('click',    onCloseClick);
    refs.speedBtn?.addEventListener('click',    onSpeedClick);
    refs.volSlider?.addEventListener('input',   onVolumeInput);
    refs.seekWrap?.addEventListener('mousedown',  onSeekMouseDown);
    refs.seekWrap?.addEventListener('touchstart', onSeekTouchStart, { passive: true });
    refs.seekWrap?.addEventListener('keydown',    onSeekKeyDown);
    document.addEventListener('mousemove', onDocMouseMove);
    document.addEventListener('mouseup',   onDocMouseUp);
    document.addEventListener('touchend',  onDocTouchEnd);
    document.addEventListener('keydown',   onDocKeyDown);

    return function dispose(): void {
        refs.apPlayPause?.removeEventListener('click', onPlayPauseClick);
        refs.rewindBtn?.removeEventListener('click',   onRewindClick);
        refs.forwardBtn?.removeEventListener('click',  onForwardClick);
        refs.closeBtn?.removeEventListener('click',    onCloseClick);
        refs.speedBtn?.removeEventListener('click',    onSpeedClick);
        refs.volSlider?.removeEventListener('input',   onVolumeInput);
        refs.seekWrap?.removeEventListener('mousedown',  onSeekMouseDown);
        refs.seekWrap?.removeEventListener('touchstart', onSeekTouchStart);
        refs.seekWrap?.removeEventListener('keydown',    onSeekKeyDown);
        document.removeEventListener('mousemove', onDocMouseMove);
        document.removeEventListener('mouseup',   onDocMouseUp);
        document.removeEventListener('touchend',  onDocTouchEnd);
        document.removeEventListener('keydown',   onDocKeyDown);
    };
}

/* ── window.XOT.article.audioControls attachment ───────────────────── */
interface XotArticleAudioControls {
    init: typeof initAudioControls;
}

declare global {
    interface Window {
        XOT?: {
            article?: {
                text?:          unknown;
                highlight?:     unknown;
                progress?:      unknown;
                share?:         unknown;
                audio?:         unknown;
                mediaSession?:  unknown;
                dropoff?:       unknown;
                playerUI?:      unknown;
                audioSession?:  unknown;
                audioControls?: XotArticleAudioControls;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.audioControls = { init: initAudioControls };
    xot.article = article;
    window.XOT  = xot;
}
